// =============================================================================
// Signup Page - Complete registration with JWE token
// =============================================================================
// Phase 5: Frontend Implementation
// Features:
// - JWE token verification
// - Verified identifier badge
// - Phone signup: Email is REQUIRED (to prevent duplicate accounts)
// - Email signup: Phone is RECOMMENDED with skip option
// v6.0: Added PageBackground for zen-like background images
// v5.1: Added returnTo parameter preservation for post-signup redirect
// =============================================================================

import { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { UserPlus, CheckCircle, Mail, Phone, ArrowLeft, AlertTriangle, Briefcase, ClipboardList } from 'lucide-react';
import { useSignup } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/auth';
import { PublicNavbar } from '@/components/PublicNavbar';
import { PageBackground } from '@/components/ui/PageBackground';
import { PasswordFields } from '@/components/auth/PasswordFields';
import { formatPhoneForDisplay } from '@/lib/auth';
import {
  TERMS_OF_SERVICE,
  TERMS_EFFECTIVE_DATE,
  TERMS_COMPANY_INFO,
  TERMS_CLOSING_STATEMENT,
  type TermsSection,
} from '@/lib/terms-content';

type UserRole = 'AGENT' | 'TRANSACTION_COORDINATOR';

interface FormData {
  firstName: string;
  lastName: string;
  secondaryContact: string;
  companyName: string;
  licenseNumber: string;
}

export function SignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    step: authStep,
    signupToken,
    verifiedIdentifier,
    verifiedIdentifierType,
    guestPreFill,
    error: storeError,
    setError,
    reset,
    setReturnTo,
    setGuestPreFill,
  } = useAuthStore();
  const { mutate: signup, isPending } = useSignup();

  // v5.2: Pre-fill from guest data (Gatekeeper name/email)
  const guestFirstName = guestPreFill?.name?.split(' ')[0] || '';
  const guestLastName = guestPreFill?.name?.split(' ').slice(1).join(' ') || '';

  const [formData, setFormData] = useState<FormData>({
    firstName: guestFirstName,
    lastName: guestLastName,
    // Pre-fill email if primary auth was phone and guest had email
    secondaryContact: (verifiedIdentifierType === 'phone' && guestPreFill?.email) ? guestPreFill.email : '',
    companyName: '',
    licenseNumber: '',
  });

  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
  // Password auth v1 (2026-09): required on the main signup path
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const termsScrollRef = useRef<HTMLDivElement>(null);

  const handleTermsScroll = useCallback(() => {
    const el = termsScrollRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
      setHasScrolledToBottom(true);
    }
  }, []);

  // If the ToS content fits entirely within the h-48 scroll container (unlikely
  // given the length, but defensive), auto-enable the checkbox on mount.
  useLayoutEffect(() => {
    const el = termsScrollRef.current;
    if (el && el.scrollHeight <= el.clientHeight + 20) {
      setHasScrolledToBottom(true);
    }
  }, []);

  // v5.1: Capture returnTo from URL if present (for direct /signup?returnTo=... links)
  // v5.2: Capture guest name/email for form pre-fill
  useEffect(() => {
    const returnTo = searchParams.get('returnTo');
    if (returnTo && returnTo.startsWith('/')) {
      setReturnTo(returnTo);
    }

    const guestName = searchParams.get('guestName');
    const guestEmail = searchParams.get('guestEmail');
    if ((guestName || guestEmail) && !guestPreFill) {
      setGuestPreFill({
        name: guestName || '',
        email: guestEmail || '',
      });
      // Update form with URL params if form is still empty
      const firstName = guestName?.split(' ')[0] || '';
      const lastName = guestName?.split(' ').slice(1).join(' ') || '';
      setFormData((prev) => ({
        ...prev,
        firstName: prev.firstName || firstName,
        lastName: prev.lastName || lastName,
        secondaryContact: prev.secondaryContact || (verifiedIdentifierType === 'phone' && guestEmail ? guestEmail : ''),
      }));
    }
  }, [searchParams, setReturnTo, guestPreFill, setGuestPreFill, verifiedIdentifierType]);

  // Redirect if not in signup flow (no signup token)
  // v5.1: Preserve returnTo when redirecting to login
  // v5.3: Skip redirect if user just completed signup (AUTHENTICATED state).
  //       Without this check, setAuthenticated clearing signupToken triggers
  //       this effect, which races with the returnTo redirect in useSignup.
  useEffect(() => {
    if (!signupToken && authStep !== 'AUTHENTICATED') {
      const returnTo = searchParams.get('returnTo');
      if (returnTo) {
        navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`);
      } else {
        navigate('/login');
      }
    }
  }, [signupToken, authStep, navigate, searchParams]);

  // Determine if secondary contact is required based on signup method
  // Phone signup -> Email REQUIRED
  // Email signup -> Phone RECOMMENDED (can skip)
  const isSecondaryRequired = verifiedIdentifierType === 'phone';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    // Clear store error
    if (storeError) {
      setError(null);
    }
  };

  // Format phone number as user types (for secondary contact when primary is email)
  const handleSecondaryPhoneChange = (value: string) => {
    const digits = value.replace(/\D/g, '');
    let formatted = digits;

    if (digits.length <= 3) {
      formatted = digits;
    } else if (digits.length <= 6) {
      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }

    setFormData((prev) => ({ ...prev, secondaryContact: formatted }));
    if (errors.secondaryContact) {
      setErrors((prev) => ({ ...prev, secondaryContact: '' }));
    }
  };

  const validate = (allowSkipSecondary = false) => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length > 50) {
      newErrors.firstName = 'First name must be 50 characters or less';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    } else if (formData.lastName.trim().length > 50) {
      newErrors.lastName = 'Last name must be 50 characters or less';
    }

    // Password auth v1 (2026-09): client checks length + match only; the
    // server additionally enforces the common-password blocklist and
    // identity-containment (surfaced via the store error).
    if (newPassword.length < 12) {
      newErrors.password = 'Password must be at least 12 characters';
    } else if (newPassword.length > 128) {
      newErrors.password = 'Password must be at most 128 characters';
    } else if (newPassword !== confirmPassword) {
      newErrors.password = 'Passwords do not match';
    }

    // Agent-specific required fields
    if (selectedRole === 'AGENT') {
      if (!formData.companyName.trim()) {
        newErrors.companyName = 'Brokerage is required';
      }
      if (!formData.licenseNumber.trim()) {
        newErrors.licenseNumber = 'License number is required';
      }
    }

    // Secondary contact validation
    if (verifiedIdentifierType === 'phone') {
      // Phone signup -> Email is REQUIRED
      if (!formData.secondaryContact.trim()) {
        newErrors.secondaryContact = 'Email is required to secure your account';
      } else if (!/^\S+@\S+\.\S+$/.test(formData.secondaryContact.trim())) {
        newErrors.secondaryContact = 'Please enter a valid email address';
      }
    } else {
      // Email signup -> Phone is recommended but can be skipped
      if (formData.secondaryContact.trim()) {
        const digits = formData.secondaryContact.replace(/\D/g, '');
        if (digits.length !== 10) {
          newErrors.secondaryContact = 'Please enter a valid 10-digit phone number';
        }
      } else if (!allowSkipSecondary) {
        // If not explicitly skipping, and no phone provided, don't add error
        // but we'll show skip confirmation dialog instead
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent, skipSecondary = false) => {
    e.preventDefault();
    if (!signupToken) return;

    // For email signup without phone, show skip confirmation
    if (verifiedIdentifierType === 'email' && !formData.secondaryContact.trim() && !skipSecondary) {
      setShowSkipConfirm(true);
      return;
    }

    if (!validate(skipSecondary)) return;

    // Build signup data
    const signupData: {
      signupToken: string;
      firstName: string;
      lastName: string;
      password: string;
      email?: string;
      phone?: string;
      companyName?: string;
      licenseNumber?: string;
      userType?: UserRole;
      termsAccepted: boolean;
    } = {
      signupToken,
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      password: newPassword,
      userType: selectedRole || undefined,
      termsAccepted: true,
    };

    // Add secondary contact if provided
    if (formData.secondaryContact.trim()) {
      if (verifiedIdentifierType === 'phone') {
        // Primary was phone, secondary is email
        signupData.email = formData.secondaryContact.trim().toLowerCase();
      } else {
        // Primary was email, secondary is phone - format it
        const digits = formData.secondaryContact.replace(/\D/g, '');
        signupData.phone = `+1${digits}`;
      }
    }

    // Add optional fields
    if (formData.companyName.trim()) {
      signupData.companyName = formData.companyName.trim();
    }
    if (formData.licenseNumber.trim()) {
      signupData.licenseNumber = formData.licenseNumber.trim();
    }

    signup(signupData);
  };

  const handleSkipConfirm = (e: React.FormEvent) => {
    setShowSkipConfirm(false);
    handleSubmit(e, true);
  };

  const handleBack = () => {
    reset();
    navigate('/login');
  };

  // Don't render if no signup token
  if (!signupToken) {
    return null;
  }

  // Display the verified identifier
  const displayVerified =
    verifiedIdentifierType === 'phone' && verifiedIdentifier
      ? formatPhoneForDisplay(verifiedIdentifier)
      : verifiedIdentifier;

  const secondaryLabel = verifiedIdentifierType === 'phone' ? 'Email address' : 'Phone number';
  const secondaryPlaceholder =
    verifiedIdentifierType === 'phone' ? 'you@example.com' : '(555) 123-4567';
  const secondaryType = verifiedIdentifierType === 'phone' ? 'email' : 'tel';
  const SecondaryIcon = verifiedIdentifierType === 'phone' ? Mail : Phone;

  return (
    <PageBackground>
      <div className="min-h-screen">
        <PublicNavbar />

      <div className="flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="mt-6 text-center text-2xl font-semibold text-gray-900">
            Complete your profile
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Already have an account?{' '}
            <Link
              to="/login"
              className="font-medium bg-gradient-to-r from-[#17DDAB] to-[#009FE4] bg-clip-text text-transparent hover:opacity-80"
            >
              Sign in
            </Link>
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            {/* Verified Identifier Badge */}
            {verifiedIdentifier && (
              <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-green-800">
                      {verifiedIdentifierType === 'phone' ? 'Phone verified' : 'Email verified'}
                    </p>
                    <p className="text-sm text-green-700 truncate">{displayVerified}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Skip Confirmation Dialog */}
            {showSkipConfirm && (
              <div className="mb-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex gap-3">
                  <div className="flex-shrink-0">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-800">
                      Skip adding phone number?
                    </p>
                    <p className="mt-1 text-sm text-amber-700">
                      Without a phone number, you won&apos;t be able to receive SMS notifications
                      and may have limited account recovery options.
                    </p>
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        onClick={() => setShowSkipConfirm(false)}
                        className="px-3 py-1.5 text-sm font-medium text-amber-800 bg-amber-100 rounded-md hover:bg-amber-200"
                      >
                        Go back
                      </button>
                      <button
                        type="button"
                        onClick={handleSkipConfirm}
                        className="px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        Skip anyway
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Role Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                I am a... <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setSelectedRole('AGENT')}
                  className={`flex flex-col items-center p-4 rounded-lg border-2 transition-all ${
                    selectedRole === 'AGENT'
                      ? 'border-[#009FE4] bg-blue-50 ring-1 ring-[#009FE4]'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <Briefcase className={`w-6 h-6 mb-2 ${selectedRole === 'AGENT' ? 'text-[#009FE4]' : 'text-gray-400'}`} />
                  <span className={`text-sm font-medium ${selectedRole === 'AGENT' ? 'text-[#009FE4]' : 'text-gray-700'}`}>
                    Real Estate Agent
                  </span>
                  <span className="text-xs text-gray-500 mt-1 text-center">
                    I represent buyers or sellers
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedRole('TRANSACTION_COORDINATOR')}
                  className={`flex flex-col items-center p-4 rounded-lg border-2 transition-all ${
                    selectedRole === 'TRANSACTION_COORDINATOR'
                      ? 'border-[#009FE4] bg-blue-50 ring-1 ring-[#009FE4]'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <ClipboardList className={`w-6 h-6 mb-2 ${selectedRole === 'TRANSACTION_COORDINATOR' ? 'text-[#009FE4]' : 'text-gray-400'}`} />
                  <span className={`text-sm font-medium ${selectedRole === 'TRANSACTION_COORDINATOR' ? 'text-[#009FE4]' : 'text-gray-700'}`}>
                    Transaction Coordinator
                  </span>
                  <span className="text-xs text-gray-500 mt-1 text-center">
                    I manage transactions for agents
                  </span>
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                    First name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    id="firstName"
                    autoComplete="given-name"
                    value={formData.firstName}
                    onChange={handleChange}
                    disabled={isPending}
                    className={`mt-1 block w-full px-3 py-2 border ${
                      errors.firstName ? 'border-red-500' : 'border-gray-300'
                    } rounded-md shadow-sm focus:outline-none focus:ring-[#009FE4] focus:border-[#009FE4] disabled:bg-gray-50 disabled:opacity-50`}
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                    Last name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    id="lastName"
                    autoComplete="family-name"
                    value={formData.lastName}
                    onChange={handleChange}
                    disabled={isPending}
                    className={`mt-1 block w-full px-3 py-2 border ${
                      errors.lastName ? 'border-red-500' : 'border-gray-300'
                    } rounded-md shadow-sm focus:outline-none focus:ring-[#009FE4] focus:border-[#009FE4] disabled:bg-gray-50 disabled:opacity-50`}
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                  )}
                </div>
              </div>

              {/* Password (v1, 2026-09) — required; server enforces blocklist */}
              <div>
                <PasswordFields
                  newPassword={newPassword}
                  confirmPassword={confirmPassword}
                  onNewPasswordChange={setNewPassword}
                  onConfirmPasswordChange={setConfirmPassword}
                  error={errors.password}
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                )}
              </div>

              {/* Secondary Contact - Required for phone signup, Recommended for email signup */}
              <div>
                <label
                  htmlFor="secondaryContact"
                  className="block text-sm font-medium text-gray-700"
                >
                  <span className="flex items-center gap-1">
                    <SecondaryIcon className="w-4 h-4" />
                    {secondaryLabel}
                    {isSecondaryRequired ? (
                      <span className="text-red-500">*</span>
                    ) : (
                      <span className="text-amber-600 font-normal">(recommended)</span>
                    )}
                  </span>
                </label>
                <input
                  type={secondaryType}
                  name="secondaryContact"
                  id="secondaryContact"
                  autoComplete={verifiedIdentifierType === 'phone' ? 'email' : 'tel'}
                  value={formData.secondaryContact}
                  onChange={
                    verifiedIdentifierType === 'email'
                      ? (e) => handleSecondaryPhoneChange(e.target.value)
                      : handleChange
                  }
                  placeholder={secondaryPlaceholder}
                  disabled={isPending}
                  className={`mt-1 block w-full px-3 py-2 border ${
                    errors.secondaryContact ? 'border-red-500' : 'border-gray-300'
                  } rounded-md shadow-sm focus:outline-none focus:ring-[#009FE4] focus:border-[#009FE4] disabled:bg-gray-50 disabled:opacity-50`}
                />
                {errors.secondaryContact && (
                  <p className="mt-1 text-sm text-red-600">{errors.secondaryContact}</p>
                )}
                {/* Contextual help text */}
                {verifiedIdentifierType === 'phone' ? (
                  <p className="mt-1 text-xs text-gray-600">
                    Email is required to secure your account and enable login via email
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-amber-600">
                    Adding your phone helps you recover your account and receive important notifications
                  </p>
                )}
              </div>

              {/* Brokerage / Company */}
              <div>
                <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
                  {selectedRole === 'AGENT' ? (
                    <>Brokerage <span className="text-red-500">*</span></>
                  ) : (
                    <>Company <span className="text-gray-400 font-normal">(optional)</span></>
                  )}
                </label>
                <input
                  type="text"
                  name="companyName"
                  id="companyName"
                  autoComplete="organization"
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder={selectedRole === 'AGENT' ? 'Your brokerage name' : 'Your company name'}
                  disabled={isPending}
                  className={`mt-1 block w-full px-3 py-2 border ${
                    errors.companyName ? 'border-red-500' : 'border-gray-300'
                  } rounded-md shadow-sm focus:outline-none focus:ring-[#009FE4] focus:border-[#009FE4] disabled:bg-gray-50 disabled:opacity-50`}
                />
                {errors.companyName && (
                  <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>
                )}
              </div>

              {/* License Number */}
              <div>
                <label htmlFor="licenseNumber" className="block text-sm font-medium text-gray-700">
                  {selectedRole === 'AGENT' ? (
                    <>License number <span className="text-red-500">*</span></>
                  ) : (
                    <>License number <span className="text-gray-400 font-normal">(optional)</span></>
                  )}
                </label>
                <input
                  type="text"
                  name="licenseNumber"
                  id="licenseNumber"
                  value={formData.licenseNumber}
                  onChange={handleChange}
                  placeholder="DRE license number"
                  disabled={isPending}
                  className={`mt-1 block w-full px-3 py-2 border ${
                    errors.licenseNumber ? 'border-red-500' : 'border-gray-300'
                  } rounded-md shadow-sm focus:outline-none focus:ring-[#009FE4] focus:border-[#009FE4] disabled:bg-gray-50 disabled:opacity-50`}
                />
                {errors.licenseNumber && (
                  <p className="mt-1 text-sm text-red-600">{errors.licenseNumber}</p>
                )}
              </div>

              {/* Terms of Service - Forced Scroll + Checkbox */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Terms of Service <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div
                    ref={termsScrollRef}
                    onScroll={handleTermsScroll}
                    className="h-48 overflow-y-auto border border-gray-300 rounded-md p-4 text-xs text-gray-700 leading-relaxed bg-gray-50"
                  >
                    <div className="text-center mb-3">
                      <p className="font-bold text-gray-900 uppercase text-[10px]">{TERMS_COMPANY_INFO.name}</p>
                      <p className="font-bold text-gray-900 uppercase text-[10px]">Terms of Service</p>
                      <p className="text-gray-500 text-[10px]">Effective Date: {TERMS_EFFECTIVE_DATE}</p>
                    </div>
                    {/* SAFETY: dangerouslySetInnerHTML is used because ToS content contains
                        <strong> tags. Content is compile-time static, never from user input. */}
                    {TERMS_OF_SERVICE.map((section: TermsSection) => (
                      <div key={section.number} className="mb-2">
                        <p className="font-semibold text-gray-800">
                          {section.number}. {section.title}
                        </p>
                        {section.content && (
                          <p className="whitespace-pre-line" dangerouslySetInnerHTML={{ __html: section.content }} />
                        )}
                        {section.subsections?.map((sub: TermsSection) => (
                          <div key={sub.number} className="ml-3 mt-1">
                            <p className="font-medium text-gray-800">
                              {sub.number} {sub.title}
                            </p>
                            <p className="whitespace-pre-line" dangerouslySetInnerHTML={{ __html: sub.content }} />
                          </div>
                        ))}
                      </div>
                    ))}
                    <div className="mt-3 pt-2 border-t border-gray-300">
                      <p className="font-semibold text-gray-900 text-[10px]">{TERMS_CLOSING_STATEMENT}</p>
                    </div>
                  </div>
                  {!hasScrolledToBottom && (
                    <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-gray-50 to-transparent pointer-events-none flex items-end justify-center pb-1">
                      <span className="text-xs text-gray-400 animate-bounce">
                        ↓ Scroll to read all terms
                      </span>
                    </div>
                  )}
                </div>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    disabled={!hasScrolledToBottom || isPending}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#009FE4] focus:ring-[#009FE4] disabled:opacity-50"
                  />
                  <span className={`text-sm ${hasScrolledToBottom ? 'text-gray-700' : 'text-gray-400'}`}>
                    I have read and agree to the Terms of Service
                  </span>
                </label>
              </div>

              {/* Error Display */}
              {storeError && (
                <div className="rounded-md bg-red-50 p-3 border border-red-200">
                  <p className="text-sm text-red-700">{storeError}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isPending || !termsAccepted || !selectedRole}
                className="w-full flex justify-center items-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-[#17DDAB] to-[#009FE4] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009FE4] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isPending ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Creating Account...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Create Account
                  </>
                )}
              </button>
            </form>

            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={handleBack}
                disabled={isPending}
                className="flex items-center justify-center gap-1 mx-auto text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50"
              >
                <ArrowLeft className="w-3 h-3" />
                Start over
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>
    </PageBackground>
  );
}
